import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMerchantNetwork } from '@/api/hooks/useMerchants';
import FullMap, { PRIMARY, VEHICLE_COLOURS, type FullMapHandle } from '@/components/maps/FullMap';
import MapToolbar, { type MapMode } from '@/components/maps/MapToolbar';
import DetectiveChips from '@/components/ai/DetectiveChips';
import RoutePanel, { type RouteStop } from '@/components/maps/RoutePanel';
import VroomPanel from '@/components/maps/VroomPanel';
import PoiLayerPanel from '@/components/maps/PoiLayerPanel';
import IsochronePanel from '@/components/maps/IsochronePanel';
import GeofencePanel, { type GeofenceDrawState } from '@/components/maps/GeofencePanel';
import HeatmapPanel from '@/components/maps/HeatmapPanel';
import VehicleReplayPanel from '@/components/maps/VehicleReplayPanel';
import SearchBar from '@/components/maps/SearchBar';
import { decodePolyline } from '@/lib/polyline';
import { useOsmGeofences } from '@/api/hooks/useOsmGeofences';
import { useHeatmapMutation } from '@/api/hooks/useHeatmap';
import { useMapActionsStore } from '@/store/mapActionsStore';
import type {
  Geofence,
  IsochroneFeatureCollection,
  RouteResponse,
  UserPoiCollection,
  VroomResponse,
} from '@/types/osm';
import type { HeatmapMetric } from '@/types/askAi';
import { cn } from '@/lib/utils';

function newStop(): RouteStop {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label: '', lng: null, lat: null };
}

const DEFAULT_OSM_CATS = new Set<string>(['fuel']);

export default function Maps() {
  const merchants = useMerchantNetwork();
  const fences = useOsmGeofences();
  const mapRef = useRef<FullMapHandle | null>(null);
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<MapMode>('route');
  const [panelOpen, setPanelOpen] = useState(true);
  const [pendingClick, setPendingClick] = useState<{ lng: number; lat: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ lng: number; lat: number; x: number; y: number } | null>(null);

  const [showMerchants, setShowMerchants] = useState(true);
  const [showUserPois, setShowUserPois] = useState(true);
  const [showOsmPois, setShowOsmPois] = useState(true);
  const [enabledOsmCategories, setEnabledOsmCategories] = useState<Set<string>>(DEFAULT_OSM_CATS);
  const [userPois, setUserPois] = useState<UserPoiCollection | null>(null);

  const [stops, setStops] = useState<RouteStop[]>(() => [newStop(), newStop()]);
  const [routeResp, setRouteResp] = useState<RouteResponse | null>(null);
  const [vroomResp, setVroomResp] = useState<VroomResponse | null>(null);
  const [isoFc, setIsoFc] = useState<IsochroneFeatureCollection | null>(null);
  const [isoPoint, setIsoPoint] = useState<{ lng: number; lat: number } | null>(null);
  const [drawState, setDrawState] = useState<GeofenceDrawState>({ active: false, vertices: [] });
  const [is3D, setIs3D] = useState(false);
  // Registration the AI asked to replay (grounded `replay_vehicle` action).
  // Drives VehicleReplayPanel via panel state — NOT via a FullMap handle.
  const [replayReg, setReplayReg] = useState<string | null>(null);
  const [replayAutoplay, setReplayAutoplay] = useState(false);
  const [activeHeatmaps, setActiveHeatmaps] = useState<Set<HeatmapMetric>>(new Set());
  const [heatmapWindowDays, setHeatmapWindowDays] = useState(30);
  const [heatmapGridKm, setHeatmapGridKm] = useState(10);
  const heatmapMutation = useHeatmapMutation();
  const consumeMapActions = useMapActionsStore((s) => s.consume);
  const pendingMapActions = useMapActionsStore((s) => s.pendingActions);

  const filteredMerchants = useMemo(() => merchants.data?.merchants ?? [], [merchants.data]);
  const fenceList: Geofence[] = useMemo(() => fences.data ?? [], [fences.data]);

  // Deep link: /maps?vehicle=<REG>&replay=1&autoplay=1
  //
  // Same three-line hand-off the grounded `replay_vehicle` AI action uses
  // below — mode, panel, reg — so the registration resolves through
  // VehicleReplayPanel's existing `requestedReg` → `selectVehicle` path rather
  // than a second lookup. `vehicle` alone is enough; `replay=1` is accepted for
  // readability of the shared link.
  const deepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const key = searchParams.toString();
    if (deepLinkRef.current === key) return;
    deepLinkRef.current = key;

    const reg = searchParams.get('vehicle')?.trim().toUpperCase();
    if (!reg) return;

    setMode('replay');
    setPanelOpen(true);
    setReplayAutoplay(searchParams.get('autoplay') === '1');
    setReplayReg(reg);
  }, [searchParams]);

  const consumePendingClick = useCallback(() => setPendingClick(null), []);

  const loadHeatmap = useCallback(
    async (metric: HeatmapMetric) => {
      const handle = mapRef.current;
      if (!handle) return;
      const until = new Date();
      const since = new Date(until.getTime() - heatmapWindowDays * 86400 * 1000);
      try {
        const resp = await heatmapMutation.mutateAsync({
          metric,
          since: since.toISOString(),
          until: until.toISOString(),
          grid_km: heatmapGridKm,
        });
        handle.addHeatmap(metric, resp.features, resp.max_weight, resp.color_scheme);
        setActiveHeatmaps((prev) => new Set(prev).add(metric));
      } catch {
        /* surfaced via mutation.error in the panel chrome */
      }
    },
    [heatmapMutation, heatmapWindowDays, heatmapGridKm],
  );

  const removeHeatmap = useCallback((metric: HeatmapMetric) => {
    mapRef.current?.removeHeatmap(metric);
    setActiveHeatmaps((prev) => {
      if (!prev.has(metric)) return prev;
      const next = new Set(prev);
      next.delete(metric);
      return next;
    });
  }, []);

  const toggleHeatmap = useCallback(
    (metric: HeatmapMetric) => {
      if (activeHeatmaps.has(metric)) removeHeatmap(metric);
      else void loadHeatmap(metric);
    },
    [activeHeatmaps, removeHeatmap, loadHeatmap],
  );

  function toggle3D() {
    const next = !is3D;
    setIs3D(next);
    mapRef.current?.set3D(next);
  }

  useEffect(() => {
    if (pendingMapActions.length === 0) return;
    const handle = mapRef.current;
    if (!handle) return;
    const drained = consumeMapActions();
    for (const action of drained) {
      if (action.action === 'show_heatmap' && action.metric) {
        if (!handle.hasHeatmap(action.metric)) {
          void loadHeatmap(action.metric);
        }
        continue;
      }
      if (action.action === 'set_3d') {
        const on = (action.pitch ?? 60) > 0;
        setIs3D(on);
        handle.set3D(on);
        continue;
      }
      // Grounded forensic replay — drive the panel, not the FullMap handle:
      // the replay logic lives entirely in VehicleReplayPanel/useVehicleReplay.
      if (action.action === 'replay_vehicle') {
        if (action.registration) {
          setMode('replay');
          setPanelOpen(true);
          setReplayReg(action.registration);
        }
        continue;
      }
      // plot_fraud_points (grounded pins) + fit_bounds/zoom/drop_pin all go
      // through the imperative handle.
      handle.applyAction(action);
    }
  }, [pendingMapActions, consumeMapActions, loadHeatmap]);

  function handleMapClick(p: { lng: number; lat: number; rightClick: boolean }) {
    if (p.rightClick) {
      setContextMenu({ lng: p.lng, lat: p.lat, x: 0, y: 0 });
      return;
    }
    setPendingClick({ lng: p.lng, lat: p.lat });
  }

  useEffect(() => {
    function onContext(e: MouseEvent) {
      if (!contextMenu) return;
      setContextMenu((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null));
    }
    if (contextMenu && contextMenu.x === 0) document.addEventListener('mousemove', onContext);
    return () => document.removeEventListener('mousemove', onContext);
  }, [contextMenu]);

  function handleSearchPick(lng: number, lat: number) {
    mapRef.current?.flyTo(lng, lat, 14);
  }

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const SRC = 'fb-route-line';
    const LAYER = 'fb-route-line-layer';
    const HALO = 'fb-route-line-halo';
    const markersAttr = (map as unknown as { __fbRouteMarkers?: maplibregl.Marker[] }).__fbRouteMarkers ?? [];
    markersAttr.forEach((m) => m.remove());
    if (map.getLayer(HALO)) map.removeLayer(HALO);
    if (map.getLayer(LAYER)) map.removeLayer(LAYER);
    if (map.getSource(SRC)) map.removeSource(SRC);

    if (!routeResp) {
      (map as unknown as { __fbRouteMarkers?: maplibregl.Marker[] }).__fbRouteMarkers = [];
      return;
    }
    const all: number[][] = [];
    for (const leg of routeResp.trip.legs) {
      const pts = decodePolyline(leg.shape, 6);
      if (all.length && pts.length) pts.shift();
      all.push(...pts);
    }
    if (all.length === 0) return;
    map.addSource(SRC, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: all },
      } as never,
    });
    map.addLayer({
      id: HALO,
      type: 'line',
      source: SRC,
      paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.6 },
    });
    map.addLayer({
      id: LAYER,
      type: 'line',
      source: SRC,
      paint: { 'line-color': PRIMARY, 'line-width': 5 },
    });
    const ms: maplibregl.Marker[] = [];
    routeResp.trip.locations?.forEach((loc, i) => {
      const el = document.createElement('div');
      el.style.cssText =
        `width:22px;height:22px;border-radius:50%;background:${PRIMARY};color:white;` +
        'font-family:Inter,system-ui,sans-serif;font-weight:600;font-size:11px;' +
        'display:flex;align-items:center;justify-content:center;border:2px solid white;' +
        'box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:pointer';
      el.textContent = String(i + 1);
      const m = new maplibregl.Marker({ element: el }).setLngLat([loc.lon, loc.lat]).addTo(map);
      ms.push(m);
    });
    (map as unknown as { __fbRouteMarkers?: maplibregl.Marker[] }).__fbRouteMarkers = ms;

    const lngs = all.map((c) => c[0]);
    const lats = all.map((c) => c[1]);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 60, duration: 500 },
    );
  }, [routeResp]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const layers = map.getStyle()?.layers ?? [];
    for (const l of layers) {
      if (l.id.startsWith('fb-vroom-')) map.removeLayer(l.id);
    }
    const sources = Object.keys(map.getStyle()?.sources ?? {});
    for (const sid of sources) {
      if (sid.startsWith('fb-vroom-')) map.removeSource(sid);
    }
    const prev = (map as unknown as { __fbVroomMarkers?: maplibregl.Marker[] }).__fbVroomMarkers ?? [];
    prev.forEach((m) => m.remove());
    if (!vroomResp) {
      (map as unknown as { __fbVroomMarkers?: maplibregl.Marker[] }).__fbVroomMarkers = [];
      return;
    }
    const ms: maplibregl.Marker[] = [];
    const allLngs: number[] = [];
    const allLats: number[] = [];
    vroomResp.routes.forEach((r, i) => {
      const colour = VEHICLE_COLOURS[i % VEHICLE_COLOURS.length];
      if (r.geometry) {
        const coords = decodePolyline(r.geometry, 5);
        if (coords.length) {
          const sid = `fb-vroom-src-${r.vehicle}`;
          const lid = `fb-vroom-line-${r.vehicle}`;
          map.addSource(sid, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates: coords },
            } as never,
          });
          map.addLayer({
            id: lid,
            type: 'line',
            source: sid,
            paint: { 'line-color': colour, 'line-width': 5, 'line-opacity': 0.85 },
          });
          coords.forEach(([x, y]) => {
            allLngs.push(x);
            allLats.push(y);
          });
        }
      }
      let stepIdx = 0;
      for (const s of r.steps) {
        if (!s.location) continue;
        stepIdx++;
        const isVehicle = s.type === 'start' || s.type === 'end';
        const el = document.createElement('div');
        el.style.cssText = `width:14px;height:14px;background:${colour};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:pointer;${
          isVehicle ? 'border-radius:2px' : 'border-radius:50%'
        }`;
        const popup = new maplibregl.Popup({ offset: 12 }).setHTML(
          `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px"><strong>Vehicle ${r.vehicle} · stop ${stepIdx}</strong><br>${s.type}${s.id != null ? ` (job ${s.id})` : ''}<br>arrival: ${(s.arrival / 60).toFixed(0)} min</div>`,
        );
        ms.push(
          new maplibregl.Marker({ element: el }).setLngLat(s.location).setPopup(popup).addTo(map),
        );
      }
    });
    (map as unknown as { __fbVroomMarkers?: maplibregl.Marker[] }).__fbVroomMarkers = ms;
    if (allLngs.length) {
      map.fitBounds(
        [
          [Math.min(...allLngs), Math.min(...allLats)],
          [Math.max(...allLngs), Math.max(...allLats)],
        ],
        { padding: 60, duration: 500 },
      );
    }
  }, [vroomResp]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const SRC = 'fb-iso';
    const FILL = 'fb-iso-fill';
    const LINE = 'fb-iso-line';
    if (map.getLayer(LINE)) map.removeLayer(LINE);
    if (map.getLayer(FILL)) map.removeLayer(FILL);
    if (map.getSource(SRC)) map.removeSource(SRC);
    if (!isoFc) return;
    map.addSource(SRC, { type: 'geojson', data: isoFc as never });
    map.addLayer({
      id: FILL,
      type: 'fill',
      source: SRC,
      paint: {
        'fill-color': ['coalesce', ['get', 'fill'], ['get', 'color'], '#3b82f6'] as never,
        'fill-opacity': 0.25,
      },
    });
    map.addLayer({
      id: LINE,
      type: 'line',
      source: SRC,
      paint: {
        'line-color': ['coalesce', ['get', 'fill'], ['get', 'color'], '#3b82f6'] as never,
        'line-width': 1.5,
        'line-opacity': 0.7,
      },
    });
  }, [isoFc]);

  function zoomToFence(g: Geofence) {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const ring =
      g.geometry.type === 'Polygon'
        ? (g.geometry.coordinates[0] as number[][])
        : ((g.geometry.coordinates as number[][][][])[0]?.[0] as number[][]) ?? [];
    if (!ring.length) return;
    const lngs = ring.map((c) => c[0]);
    const lats = ring.map((c) => c[1]);
    map.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      { padding: 80, duration: 500 },
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Full-bleed map page — no PageHeader to hang the Detective row under,
          so it gets its own thin strip above the toolbar/map/panel split. */}
      <DetectiveChips
        module="maps"
        className="shrink-0 border-b bg-background px-3 py-2"
      />
      <div className="flex min-h-0 flex-1">
      <MapToolbar
        mode={mode}
        onChange={setMode}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((v) => !v)}
        is3D={is3D}
        onToggle3D={toggle3D}
      />

      <div className="relative min-w-0 flex-1">
        <FullMap
          ref={mapRef}
          onMapClick={handleMapClick}
          merchants={filteredMerchants}
          showMerchants={showMerchants}
          userPois={userPois}
          showUserPois={showUserPois}
          showOsmPois={showOsmPois}
          enabledOsmCategories={enabledOsmCategories}
          geofences={fenceList}
          drawPreviewRing={drawState.active ? drawState.vertices : null}
        />
        <SearchBar onPick={handleSearchPick} />


        {contextMenu && contextMenu.x !== 0 && (
          <div
            className="fixed z-30 min-w-[180px] rounded-md border bg-popover p-1 text-xs shadow-md"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onMouseLeave={() => setContextMenu(null)}
          >
            <ContextMenuItem
              onClick={() => {
                setMode('route');
                setPanelOpen(true);
                const next = stops.slice();
                next[0] = {
                  ...next[0],
                  lng: contextMenu.lng,
                  lat: contextMenu.lat,
                  label: `${contextMenu.lat.toFixed(4)}, ${contextMenu.lng.toFixed(4)}`,
                };
                setStops(next);
                setContextMenu(null);
              }}
            >
              Set as origin
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                setMode('route');
                setPanelOpen(true);
                const next = stops.slice();
                const last = Math.max(0, next.length - 1);
                next[last] = {
                  ...next[last],
                  lng: contextMenu.lng,
                  lat: contextMenu.lat,
                  label: `${contextMenu.lat.toFixed(4)}, ${contextMenu.lng.toFixed(4)}`,
                };
                setStops(next);
                setContextMenu(null);
              }}
            >
              Set as destination
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                setMode('route');
                setPanelOpen(true);
                setStops([
                  ...stops,
                  {
                    id: `${Date.now()}`,
                    label: `${contextMenu.lat.toFixed(4)}, ${contextMenu.lng.toFixed(4)}`,
                    lng: contextMenu.lng,
                    lat: contextMenu.lat,
                  },
                ]);
                setContextMenu(null);
              }}
            >
              Add stop
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                setMode('iso');
                setPanelOpen(true);
                setIsoPoint({ lng: contextMenu.lng, lat: contextMenu.lat });
                setContextMenu(null);
              }}
            >
              Isochrone seed here
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                navigator.clipboard.writeText(`${contextMenu.lat}, ${contextMenu.lng}`);
                setContextMenu(null);
              }}
            >
              Copy coordinates
            </ContextMenuItem>
          </div>
        )}
      </div>

      <aside
        className={cn(
          'relative flex h-full flex-col border-l bg-background transition-[width] duration-200',
          panelOpen ? 'w-[320px]' : 'w-0',
        )}
      >
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          className="absolute -left-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow"
          title={panelOpen ? 'Collapse' : 'Expand'}
        >
          {panelOpen ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </button>
        {panelOpen && (
          <div className="h-full overflow-hidden">
            {mode === 'route' && (
              <RoutePanel
                stops={stops}
                setStops={setStops}
                pendingClick={pendingClick}
                consumePendingClick={consumePendingClick}
                onResult={(r) => setRouteResp(r)}
                onClear={() => setRouteResp(null)}
              />
            )}
            {mode === 'vroom' && <VroomPanel onResult={setVroomResp} />}
            {mode === 'pois' && (
              <PoiLayerPanel
                enabledCategories={enabledOsmCategories}
                setEnabledCategories={setEnabledOsmCategories}
                showOsmPois={showOsmPois}
                setShowOsmPois={setShowOsmPois}
                showUserPois={showUserPois}
                setShowUserPois={setShowUserPois}
                showMerchants={showMerchants}
                setShowMerchants={setShowMerchants}
                userPois={userPois}
                setUserPois={setUserPois}
              />
            )}
            {mode === 'iso' && (
              <IsochronePanel
                pendingClick={pendingClick}
                consumePendingClick={consumePendingClick}
                point={isoPoint}
                setPoint={setIsoPoint}
                onResult={setIsoFc}
              />
            )}
            {mode === 'fence' && (
              <GeofencePanel
                drawState={drawState}
                setDrawState={setDrawState}
                pendingClick={pendingClick}
                consumePendingClick={consumePendingClick}
                onZoomTo={zoomToFence}
              />
            )}
            {mode === 'heatmap' && (
              <HeatmapPanel
                activeMetrics={activeHeatmaps}
                toggleMetric={toggleHeatmap}
                loadingMetric={
                  heatmapMutation.isPending
                    ? (heatmapMutation.variables?.metric ?? null)
                    : null
                }
                gridKm={heatmapGridKm}
                setGridKm={setHeatmapGridKm}
                windowDays={heatmapWindowDays}
                setWindowDays={setHeatmapWindowDays}
              />
            )}
            {mode === 'replay' && (
              <VehicleReplayPanel
                mapRef={mapRef}
                requestedReg={replayReg}
                autoplay={replayAutoplay}
              />
            )}
          </div>
        )}
      </aside>
      </div>
    </div>
  );
}

function ContextMenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded px-2 py-1 text-left hover:bg-accent"
    >
      {children}
    </button>
  );
}
